import axios from "axios";
import React, { useState, useEffect, useRef } from "react";
import { useHistory, useParams, Link } from "react-router-dom";
import swal from "sweetalert";
import UseFullPageLoader from "../FullPageLoader/UseFullPageLoader";
import AdminAutoComplete from "../admin/AdminAutoComplete";

const ManageProfile = () => {
  document.title = "DirtMap - Mange Profile";
  const userId = useParams();
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [userInput, setUserInput] = useState({}); // ✅ must be object, not []
  const [error, setError] = useState([]);
  const [loader, showLoader, hideLoader] = UseFullPageLoader();
  const [isDisplayed, setIsDisplayed] = useState(false);

  // dropdown data
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [truckTypes, setTruckTypes] = useState([]);

  // selected dropdown ids
  const [selectedCountryId, setSelectedCountryId] = useState("");
  const [selectedStateId, setSelectedStateId] = useState("");
  const [selectedCityId, setSelectedCityId] = useState("");

  const [truckRows, setTruckRows] = useState([
    { type_of_truck: "", no_of_truck: "" }
  ]);

  const [equipmentRows, setEquipmentRows] = useState([
    { equipment: "", quantity: "" }
  ]);

  // prefill control
  const [isPrefill, setIsPrefill] = useState(false);
  const prefillRef = useRef({ stateId: "", cityId: "" });

  const handleInput = (e) => {
    e.persist();
    setUserInput((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };


  // TRUCK
  const handleTruckChange = (index, e) => {
    const { name, value } = e.target;
    const updated = [...truckRows];
    updated[index][name] = value;
    setTruckRows(updated);
  };

  const addTruckRow = () => {
    setTruckRows([...truckRows, { type_of_truck: "", no_of_truck: "" }]);
  };

  const removeTruckRow = (index) => {
    const updated = [...truckRows];
    updated.splice(index, 1);
    setTruckRows(updated);
  };

  // EQUIPMENT
  const handleEquipmentChange = (index, e) => {
    const { name, value } = e.target;
    const updated = [...equipmentRows];
    updated[index][name] = value;
    setEquipmentRows(updated);
  };

  const addEquipmentRow = () => {
    setEquipmentRows([...equipmentRows, { equipment: "", quantity: "" }]);
  };

  const removeEquipmentRow = (index) => {
    const updated = [...equipmentRows];
    updated.splice(index, 1);
    setEquipmentRows(updated);
  };

  useEffect(() => {
    const t = setTimeout(() => setIsDisplayed(true), 1200);
    return () => clearTimeout(t);
  }, []);

  const setCoodinationValueAddress = (results, ll) => {
    const fullAddress = results?.[0]?.formatted_address || "";

    setUserInput((prev) => ({
      ...prev,
      address: fullAddress,
      latitude: ll?.lat,
      longitude: ll?.lng,
    }));
  };

  // ✅ Load countries
  useEffect(() => {
    axios
      .get("/api/countryStateCity/list")
      .then((res) => {
        if (res.data?.success) setCountries(res.data?.result?.items || []);
      })
      .catch(console.log);
  }, []);

  // ✅ Load profile + prefill ids
  useEffect(() => {
    if (!localStorage.getItem("auth_token")) {
      history.push("/login");
      return;
    }

    axios
      .get(`api/get/profile/${userId.id}`)
      .then((res) => {
        if (res.data?.result) {
          const u = res.data.result || {};

          const countryId = u.country ? String(u.country) : "";
          const stateId = u.state ? String(u.state) : "";
          const cityId = u.city ? String(u.city) : "";

          if (u.trucks && u.trucks.length > 0) {
            setTruckRows(u.trucks);
          }

          if (u.equipments && u.equipments.length > 0) {
            setEquipmentRows(u.equipments);
          }

          // ✅ enable prefill
          setIsPrefill(true);
          prefillRef.current.stateId = stateId;
          prefillRef.current.cityId = cityId;

          setUserInput({
            ...u,
            country: countryId,
            state: stateId,
            city: cityId,
          });

          // ✅ ONLY set country now
          setSelectedCountryId(countryId);

          // ❌ DO NOT setSelectedStateId / setSelectedCityId here (causes reset race)
          setSelectedStateId("");
          setSelectedCityId("");
        } else {
          swal("warning", res.data.message || "Profile not found", "error");
          history.push("/login");
        }
      })
      .catch(console.log)
      .finally(() => setLoading(false));
  }, [userId.id, history]);

  // ✅ When country changes -> load states
  useEffect(() => {
    if (!selectedCountryId) {
      setStates([]);
      setCities([]);
      setSelectedStateId("");
      setSelectedCityId("");
      setUserInput((prev) => ({ ...prev, country: "", state: "", city: "" }));
      return;
    }

    axios
      .get(`/api/countryStateCity/list?country_id=${selectedCountryId}`)
      .then((res) => {
        const items = res.data?.result?.items || [];
        setStates(items);

        // keep country
        setUserInput((prev) => ({ ...prev, country: selectedCountryId }));

        if (!isPrefill) {
          // manual change -> reset
          setCities([]);
          setSelectedStateId("");
          setSelectedCityId("");
          setUserInput((prev) => ({ ...prev, state: "", city: "" }));
        } else {
          // ✅ prefill: apply saved state AFTER list loads
          const savedState = prefillRef.current.stateId;
          if (savedState && items.some((s) => String(s.id) === String(savedState))) {
            setSelectedStateId(String(savedState));
            setUserInput((prev) => ({ ...prev, state: String(savedState) }));
          }
        }
      })
      .catch(console.log);
  }, [selectedCountryId]);

  // ✅ When state changes -> load cities
  useEffect(() => {
    if (!selectedStateId) {
      setCities([]);
      setSelectedCityId("");
      setUserInput((prev) => ({ ...prev, city: "" }));
      return;
    }

    axios
      .get(`/api/countryStateCity/list?state_id=${selectedStateId}`)
      .then((res) => {
        const items = res.data?.result?.items || [];
        setCities(items);

        setUserInput((prev) => ({ ...prev, state: selectedStateId }));

        if (!isPrefill) {
          setSelectedCityId("");
          setUserInput((prev) => ({ ...prev, city: "" }));
        } else {
          // ✅ prefill: apply saved city AFTER list loads
          const savedCity = prefillRef.current.cityId;
          if (savedCity && items.some((c) => String(c.id) === String(savedCity))) {
            setSelectedCityId(String(savedCity));
            setUserInput((prev) => ({ ...prev, city: String(savedCity) }));
          }
        }
      })
      .catch(console.log);
  }, [selectedStateId]);

  // ✅ Stop prefill ONLY after cities list loads
  useEffect(() => {
    if (isPrefill && cities.length > 0) {
      setIsPrefill(false);
    }
  }, [cities, isPrefill]);

  useEffect(() => {
    axios.get("/api/truck-types")
      .then(res => {
        if (res.data.success) {
          setTruckTypes(res.data.result);
        }
      }) 
      .catch(err => console.log(err));
  }, []);

  const updateUser = (e) => {
    e.preventDefault();

    // ✅ IMPORTANT: send selected ids
    const data = {
      ...userInput,
      country: selectedCountryId,
      state: selectedStateId,
      city: selectedCityId,
      trucks: truckRows,
      equipments: equipmentRows
    };

    showLoader();
    axios.get("/sanctum/csrf-cookie").then(() => {
      axios.post(`/api/update/profile/${userId.id}`, data).then((res) => {
        if (res.data?.result) {
          hideLoader();
          swal("success", "User Updated Successfully Done", "success");
          setError([]);
          history.push("/manage-profile/" + userId.id);
        } else {
          hideLoader();
          setError(res.data.errors || []);
        }
      });
    });
  };

  if (loading) {
    return (
      <div className="fp-container">
        <img src={"/images/spinner.gif"} className="fp-loader" alt="loading" />
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card shadow mb-4">
        <section className="breadcrumb">
          <div className="container">
            <div className="row">
              <div className="col-md-12 bread-crumb">
                <Link to="/">Home</Link>
                <span>&gt;</span>Manage Profile
              </div>
            </div>
          </div>
          <hr />
        </section>

        <div className="card-body">
          <form onSubmit={updateUser}>
            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <input type="hidden" name="latitude" value={userInput.latitude || ""} />
                  <input type="hidden" name="longitude" value={userInput.longitude || ""} />

                  <label>First Name</label>
                  <input
                    type="text"
                    name="first_name"
                    onChange={handleInput}
                    value={userInput.first_name || ""}
                    className="form-control"
                    placeholder="Enter First Name"
                  />
                  <span className="validate-error-message">{error.first_name}</span>
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    name="last_name"
                    onChange={handleInput}
                    value={userInput.last_name || ""}
                    className="form-control"
                    placeholder="Enter Last Name"
                  />
                  <span className="validate-error-message">{error.last_name}</span>
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label>User Name</label>
                  <input
                    type="text"
                    name="user_name"
                    onChange={handleInput}
                    value={userInput.user_name || ""}
                    className="form-control"
                    placeholder="Enter User Name"
                  />
                  <span className="validate-error-message">{error.user_name}</span>
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label>Email</label>
                  <input disabled type="email" value={userInput.email || ""} className="form-control" />
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label>Company Name</label>
                  <input
                    type="text"
                    name="company_name"
                    onChange={handleInput}
                    value={userInput.company_name || ""}
                    className="form-control"
                    placeholder="Enter Company Name"
                  />
                  <span className="validate-error-message">{error.company_name}</span>
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group address-autocomplete-wrapper">
                  <label>Address</label>
                  {isDisplayed && (
                    <AdminAutoComplete
                      apiKey={"AIzaSyCv7m-2LTrrKnu1P38xKNBvvFJXoQGAnGg"}
                      setCoodinationValueAddress={setCoodinationValueAddress}
                      addressLocation={{
                        address: userInput.address || "",
                        latitude: userInput.latitude || "",
                        longitude: userInput.longitude || "",
                      }}
                    />
                  )}
                  <span className="validate-error-message">{error.address}</span>
                </div>
              </div>

              {/* Country */}
              <div className="col-md-6">
                <div className="form-group">
                  <label>Country</label>
                  <select
                    className="form-control"
                    value={selectedCountryId}
                    onChange={(e) => {
                      const id = e.target.value;
                      setSelectedCountryId(id);
                      setUserInput((prev) => ({ ...prev, country: id }));
                    }}
                  >
                    <option value="">Select Country</option>
                    {countries.map((c) => (
                      <option key={c.id} value={String(c.id)}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  <span className="validate-error-message">{error.country}</span>
                </div>
              </div>

              {/* State */}
              <div className="col-md-6">
                <div className="form-group">
                  <label>State</label>
                  <select
                    className="form-control"
                    value={selectedStateId}
                    disabled={!selectedCountryId}
                    onChange={(e) => {
                      const id = e.target.value;
                      setSelectedStateId(id);
                      setUserInput((prev) => ({ ...prev, state: id }));
                    }}
                  >
                    <option value="">Select State</option>
                    {states.map((s) => (
                      <option key={s.id} value={String(s.id)}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                  <span className="validate-error-message">{error.state}</span>
                </div>
              </div>

              {/* City */}
              <div className="col-md-6">
                <div className="form-group">
                  <label>City</label>
                  <select
                    className="form-control"
                    value={selectedCityId}
                    disabled={!selectedStateId}
                    onChange={(e) => {
                      const id = e.target.value;
                      setSelectedCityId(id);
                      setUserInput((prev) => ({ ...prev, city: id }));
                    }}
                  >
                    <option value="">Select City</option>
                    {cities.map((ci) => (
                      <option key={ci.id} value={String(ci.id)}>
                        {ci.name}
                      </option>
                    ))}
                  </select>
                  <span className="validate-error-message">{error.city}</span>
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label>Phone Number</label>
                  <input
                    type="text"
                    name="phone_number"
                    onChange={handleInput}
                    value={userInput.phone_number || ""}
                    className="form-control"
                    placeholder="Enter Phone Number"
                  />
                  <span className="validate-error-message">{error.phone_number}</span>
                </div>
              </div>
              {userInput.user_type === "contractor" && (
                <>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Truck Details</label>

                      {truckRows.map((row, index) => (
                        <div key={index} className="row mb-2">
                          <div className="col-md-5">
                            <select
                              name="type_of_truck"
                              value={row.type_of_truck}
                              onChange={(e) => handleTruckChange(index, e)}
                              className="form-control"
                            >
                              <option value="">Select Truck Type</option>
                              {truckTypes.map((item) => (
                                <option key={item.id} value={item.name}>
                                  {item.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="col-md-5">
                            <input
                              type="number"
                              name="no_of_truck"
                              value={row.no_of_truck}
                              onChange={(e) => handleTruckChange(index, e)}
                              className="form-control"
                              placeholder="No of Truck"
                            />
                          </div>

                          <div className="col-md-2">
                            {truckRows.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeTruckRow(index)}
                              >
                                X
                              </button>
                            )}
                          </div>
                        </div>
                      ))}

                      <button type="button" onClick={addTruckRow}>
                        + Add Truck
                      </button>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Equipment Details</label>

                      {equipmentRows.map((row, index) => (
                        <div key={index} className="row mb-2">
                          <div className="col-md-5">
                            <input
                              type="text"
                              name="equipment"
                              value={row.equipment}
                              onChange={(e) => handleEquipmentChange(index, e)}
                              className="form-control"
                              placeholder="Equipment"
                            />
                          </div>

                          <div className="col-md-5">
                            <input
                              type="number"
                              name="quantity"
                              value={row.quantity}
                              onChange={(e) => handleEquipmentChange(index, e)}
                              className="form-control"
                              placeholder="Quantity"
                            />
                          </div>

                          <div className="col-md-2">
                            {equipmentRows.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeEquipmentRow(index)}
                              >
                                X
                              </button>
                            )}
                          </div>
                        </div>
                      ))}

                      <button type="button" onClick={addEquipmentRow}>
                        + Add Equipment
                      </button>
                    </div>
                  </div>
                </>
              )}
              

              
            </div>

            <div className="row ">
              <div className="col-md-12 text-right">
                <button type="submit" className="btn btn-primary">
                  Update
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {loader}
    </div>
  );
};

export default ManageProfile;
